// ============================================================
// useTraitement — l'écran suit le déroulé, page par page.
// ------------------------------------------------------------
// Le pipeline sait tout enchaîner depuis trois sessions : dépôt →
// lecture → géométrie → brut → matière → volumes → estimation → prix.
// **L'écran ne l'appelait pas.** Sur un document de 22 pages, il fallait
// cliquer « Lire » 22 fois, créer 22 pièces, demander 22 estimations —
// soixante-six gestes pour ce que le programme sait faire seul.
//
// Ce hook est le chaînon manquant. Il interroge l'état des pages et
// s'arrête tout seul quand plus rien ne bouge.
//
// ── Pourquoi une interrogation périodique et pas un flux poussé ──
//
// Un flux d'événements serveur demanderait de tenir une connexion
// ouverte par onglet, de la rétablir après une coupure, et de gérer un
// état de connexion de plus. L'interrogation coûte une requête toutes
// les deux secondes pendant les quelques minutes que dure un lot — et
// elle s'arrête d'elle-même. Le rapport coût/complexité penche
// franchement d'un côté.
//
// ── Deux propriétés qui comptent ──
//
// **Elle s'arrête.** Tant qu'une page est en attente, en lecture ou en
// estimation, on continue ; dès que tout est stable, on cesse. Une
// interrogation qui tourne indéfiniment sur un devis fini est une fuite
// discrète qu'on ne remarque qu'au relevé de charge.
//
// **Elle survit à un rechargement.** L'état vit en base, pas ici : le
// pipeline continue même si Charles ferme l'onglet, et rouvrir la page
// retrouve la progression réelle. C'est ce qui rend l'attente
// supportable — on peut partir.

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api.js';

/** Les états où quelque chose est encore en train de se produire. */
const EN_MOUVEMENT = new Set(['en_attente', 'lecture', 'estimation']);

const INTERVALLE_MS = 2000;

export function useTraitement(devisId, { onTermine } = {}) {
  const [pages, setPages] = useState([]);
  const [actif, setActif] = useState(false);
  const minuteur = useRef(null);
  // La référence est mise à jour dans un effet, pas pendant le rendu :
  // React 19 refuse l'écriture d'une ref pendant que le composant rend,
  // et il a raison — un rendu doit être sans effet de bord.
  const termine = useRef(onTermine);
  useEffect(() => {
    termine.current = onTermine;
  }, [onTermine]);

  const lire = useCallback(async () => {
    try {
      const { pages: p } = await api.get(`/devis/${devisId}/plans/etat`);
      setPages(p ?? []);
      return (p ?? []).some((x) => EN_MOUVEMENT.has(x.etat));
    } catch {
      // Une interrogation qui échoue ne doit pas casser l'écran : le
      // traitement continue côté serveur, on réessaiera au tour suivant.
      return true;
    }
  }, [devisId]);

  const arreter = useCallback(() => {
    if (minuteur.current) clearInterval(minuteur.current);
    minuteur.current = null;
    setActif(false);
  }, []);

  const suivre = useCallback(() => {
    if (minuteur.current) return;
    setActif(true);
    minuteur.current = setInterval(async () => {
      const bouge = await lire();
      if (!bouge) {
        arreter();
        // Le devis a changé en base — pièces créées, prix calculés.
        // C'est le moment de le recharger, et une seule fois.
        termine.current?.();
      }
    }, INTERVALLE_MS);
  }, [lire, arreter]);

  /** Relance ce qui n'a pas abouti. Idempotent côté serveur. */
  const relancer = useCallback(async () => {
    await api.post(`/devis/${devisId}/plans/traitement`);
    await lire();
    suivre();
  }, [devisId, lire, suivre]);

  // À l'ouverture : on regarde une fois. Si ça bouge encore — parce que
  // Charles a rechargé pendant un lot — on suit.
  useEffect(() => {
    let vivant = true;
    lire().then((bouge) => {
      if (vivant && bouge) suivre();
    });
    return () => {
      vivant = false;
      if (minuteur.current) clearInterval(minuteur.current);
      minuteur.current = null;
    };
  }, [lire, suivre]);

  const enCours = pages.filter((p) => EN_MOUVEMENT.has(p.etat)).length;

  return { pages, actif, enCours, suivre, relancer, rafraichir: lire };
}

export default useTraitement;
